import dayjs from 'dayjs'
import Image from 'next/image'
import { useCallback, useEffect, useState } from 'react'
import CopyToClipboard from 'react-copy-to-clipboard'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { updateEntity } from '@hozana/api/actions'
import { fetchApi } from '@hozana/api/fetchApi'
import { selectEntity } from '@hozana/api/selectors'
import { HtmlParser } from '@hozana/dom/components/HtmlParser'
import { useDispatch, useSelector } from '@hozana/redux/hooks'
import { cookie } from '@hozana/storage/cookies'

import { Button } from 'elements/button/Button'
import { Div } from 'elements/layout/Div'
import { Space } from 'elements/layout/Space'
import { Icon } from 'elements/media/Icon'
import { Ellipsis } from 'elements/text/Ellipsis'
import { Link } from 'elements/text/Link'
import { P } from 'elements/text/P'
import { Alert } from 'elements/ui/Alert'

import { ICON } from 'config/icons'
import { COOKIES_CONFIG } from 'general/managers/cookies/constants'
import { COOKIES } from 'general/managers/cookies/cookieNames'

import { subscribeToPrayerMeetingMutation } from 'modules/meeting/queries'
import { TPrayerMeeting } from 'modules/meeting/types'

import { TPrayerEvent } from '../../types'

const StyledP = styled(P)`
  white-space: break-spaces;
`
type TPrayerMeetingInfoProps = {
  prayerEvent: TPrayerEvent
  prayerMeeting: TPrayerMeeting
  setTab: (tab: 'info' | 'invite') => void
}

const MARKER_IMG = '/img/map/place_big.png'

export const PrayerMeetingInfo: TFC<TPrayerMeetingInfoProps> = ({ prayerEvent, prayerMeeting, setTab }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [arePlacetailsShown, setArePlaceDetailsShown] = useState(false)
  const [isContactShow, setIsContactShow] = useState(false)
  const [isEmailCopied, setIsEmailCopied] = useState(false)
  const [isParticipant, setIsParticipant] = useState(
    prayerMeeting?.id && prayerMeeting?.id.toString() === cookie.load(COOKIES.subscribedPrayerEventMeeting),
  )
  const currentSubscription = useSelector((state) =>
    selectEntity(state, 'prayerMeeting', parseInt(cookie.load(COOKIES.subscribedPrayerEventMeeting))),
  )

  useEffect(() => {
    setIsParticipant(
      prayerMeeting?.id && prayerMeeting?.id.toString() === cookie.load(COOKIES.subscribedPrayerEventMeeting),
    )
  }, [prayerMeeting])

  const subcribeToPrayerMeeting = useCallback(() => {
    if (currentSubscription) {
      dispatch(
        //We already subscribed. We remove one participant from the old subscription
        updateEntity('prayerMeeting', currentSubscription?.id, {
          numberSubscribers: currentSubscription.numberSubscribers - 1,
        }),
      )
    } else {
      dispatch(fetchApi(subscribeToPrayerMeetingMutation(prayerMeeting.id)))
    }
    dispatch(
      updateEntity('prayerMeeting', prayerMeeting.id, {
        numberSubscribers: prayerMeeting.numberSubscribers + 1,
      }),
    )
    setIsParticipant(true)
    cookie.save(COOKIES.subscribedPrayerEventMeeting, prayerMeeting?.id, COOKIES_CONFIG.ONE_YEAR)
  }, [dispatch, prayerMeeting, currentSubscription])

  return (
    <>
      <P bold fontSize="1.5em">
        {prayerMeeting?.placeName}
      </P>
      <Space />
      {/** '+ 1' to count the meeting creator into the participants */}
      {`${prayerMeeting?.numberSubscribers + 1} ${t('event:event.map.card.participants', {
        count: prayerMeeting?.numberSubscribers + 1,
      })}`}
      <Space />
      {isParticipant && (
        <Alert hideCloseButton>
          <Div fontSize="18px" align="middle">
            <Icon name={ICON.CHECK_SOLID} m="0 10px 0 0" />
            <P bold>{t('event:event.meeting.you-participate')}</P>
          </Div>
        </Alert>
      )}
      <Space />
      <Div display="flex" m="10px 0 0 0">
        <Div w="40px">
          <Image src={MARKER_IMG} width="25px" height="25px" alt="event:event.map.card.participants" />
        </Div>
        <Div>
          <P fontSize="18px">{prayerMeeting?.placeName}</P>
          <Space />
          <P fontSize="1em" color="black50">
            {prayerMeeting?.placeAddress.formattedAddress}
          </P>
          <Link
            asButton
            underline
            color="blue"
            m="5px 0 5px 0"
            onClick={() => setArePlaceDetailsShown(!arePlacetailsShown)}
          >
            {arePlacetailsShown
              ? t('event:event.meeting.hide-address-details')
              : t('event:event.meeting.see-address-details')}
          </Link>
          {arePlacetailsShown && <P color="black50">{prayerMeeting?.placeDetails}</P>}
        </Div>
      </Div>
      <Space />
      <Div display="flex">
        <Icon name={ICON.CALENDAR} size="24px" color="orange" m="0 1em 0 0" />
        <P fontSize="18px">{dayjs(prayerMeeting?.timeStart).format('dddd DD MMMM, HH:mm')}</P>
      </Div>
      <Space h="30px" />
      <Button
        w="100%"
        colors={isParticipant ? 'darkBlue' : 'yellow'}
        onClick={() => (isParticipant ? setTab('invite') : subcribeToPrayerMeeting())}
      >
        {isParticipant ? t('event:event.meeting.invite-people') : t('event:event.meeting.participate')}
      </Button>
      <Space h="30px" />
      <P upper fontSize="20px" color="black50">
        <Icon name={ICON.TEXT} size="24px" m="0 10px 0 0" />
        {t('event:event.meeting.planning')}
      </P>
      <Space />
      <P fontSize="1em">
        <HtmlParser>{t('event:event.meeting.planning-intro', { title: prayerEvent?.title })}</HtmlParser>
      </P>
      <StyledP m="5px 0 0 0" fontSize="1em">
        <HtmlParser>{prayerMeeting?.planning ?? ''}</HtmlParser>
      </StyledP>
      <Space />
      <Div display="flex">
        <Icon name={ICON.PROFIL} size="30px" m="0 10px 0 0" color="darkBlue" />
        <Div>
          <P fontSize="20px">{`${prayerMeeting?.organizerName} ${prayerMeeting?.organizerSurname}`}</P>
          <P color="black50" fontSize="1em">
            {t('event:event.meeting.organizer-description')}
          </P>
        </Div>
      </Div>
      <Space />
      {!isContactShow && (
        <Button bg="blue" onClick={() => setIsContactShow(true)}>
          trans:event:event.meeting.organizer-contact
        </Button>
      )}
      <Space />
      {isContactShow && (
        <Div fontSize="18px">
          <CopyToClipboard
            text={prayerMeeting.organizerEmail}
            onCopy={() => {
              setIsEmailCopied(true)
            }}
          >
            <Div display="flex" align="middleBetween">
              <P>
                <a href={`mailto:${prayerMeeting.organizerEmail}`}>
                  <Ellipsis>{prayerMeeting.organizerEmail}</Ellipsis>
                </a>
              </P>

              <Button bg="white" borderColor="transparent" hoverBg="black10" color="black50" p="0.75em">
                <Icon name={ICON.COPY} size="1.5em" m="0 5px 5px 0" />
                {isEmailCopied ? t('event:word.copied') : t('event:word.copy')}
              </Button>
            </Div>
          </CopyToClipboard>
        </Div>
      )}
    </>
  )
}
